/**
 * Expanded country card for group stage with drag-to-reorder.
 * Shows image, rank number. Tap to view details, drag to reorder.
 */

import { Check, GripVertical, ImageOff, Info } from 'lucide-react';
import { useState } from 'react';
import { CountryInfoModal } from '@/components/CountryInfoModal';
import { useCountryDataContext } from '@/contexts/CountryDataContext';
import { getCountryByCode } from '@/data/countries';
import type { CountryCode } from '@/data/countries/types';
import { cn } from '@/lib/utils';
import { CountryLabel } from './CountryLabel';

interface GroupCardDisplayProps {
  code: CountryCode;
  index: number;
  isDragging?: boolean;
}

/**
 * Presentational component - used in DragOverlay during drag.
 */
export function GroupCardDisplay({
  code,
  index,
  isDragging,
}: GroupCardDisplayProps) {
  const country = getCountryByCode(code);

  if (!country) return null;

  return (
    <div
      className={cn(
        'flex items-center gap-3 p-3 rounded-xl border-2 bg-card border-primary/60 transition-all',
        isDragging && 'shadow-xl ring-2 ring-primary/50',
      )}
    >
      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-primary/10 text-primary font-bold text-sm">
        {index + 1}
      </div>
      <span className="text-2xl">{country.flag}</span>
      <span className="flex-1 font-semibold truncate">{country.name}</span>
      <GripVertical className="w-5 h-5 text-muted-foreground/50" />
    </div>
  );
}

interface GroupCardProps {
  code: CountryCode;
  index: number;
  qualifies?: boolean;
  disabled?: boolean;
  onTap?: () => void;
  dragHandleProps?: Record<string, unknown>;
}

/**
 * Expanded interactive card for ranking countries in groups.
 * Tap to view full info, drag to reorder.
 */
export function GroupCard({
  code,
  index,
  qualifies = false,
  disabled = false,
  onTap,
  dragHandleProps,
}: GroupCardProps) {
  const [showInfo, setShowInfo] = useState(false);
  const country = getCountryByCode(code);
  const { getPhotos } = useCountryDataContext();
  const photos = getPhotos(code);

  if (!country) return null;

  const handleCardClick = () => {
    if (disabled) return;
    if (onTap) {
      onTap();
    } else {
      setShowInfo(true);
    }
  };

  // Get first photo from database - only show real curated images
  const thumbnailImage = photos.length > 0 ? photos[0].photo_url : null;

  return (
    <>
      <div
        className={cn(
          'relative flex-1 rounded-xl border-2 overflow-hidden transition-all select-none flex flex-col',
          qualifies
            ? 'bg-green-50 dark:bg-green-950/30 border-green-500/50'
            : 'bg-card border-border/60 opacity-60',
          !disabled && 'cursor-pointer active:scale-[0.99]',
          disabled && 'opacity-80',
        )}
      >
        {/* Image area - fills available space */}
        <div className="flex-1 relative overflow-hidden min-h-0" onClick={handleCardClick}>
          {thumbnailImage ? (
            <img
              src={thumbnailImage}
              alt={country.name}
              className="absolute inset-0 w-full h-full object-cover"
            />
          ) : (
            <div className="absolute inset-0 bg-muted/30 flex flex-col items-center justify-center gap-2">
              <ImageOff className="w-8 h-8 text-muted-foreground/50" />
              <span className="text-xs text-muted-foreground/70">No image</span>
            </div>
          )}

          {/* Rank badge - top left */}
          <div
            className={cn(
              'absolute top-2 left-2 flex items-center justify-center gap-1 h-8 rounded-full font-bold text-sm px-2.5',
              qualifies
                ? 'bg-green-600 text-white'
                : 'bg-black/60 text-white',
            )}
          >
            {index + 1}
            {qualifies && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
          </div>

          {/* Info icon - top right */}
          <div className="absolute top-2 right-2 p-1.5 rounded-full bg-black/40 text-white/90">
            <Info className="w-4 h-4" />
          </div>

          {/* Country info overlay at bottom with drag handle */}
          <div
            {...dragHandleProps}
            className="absolute bottom-0 left-0 right-0 p-2 bg-black/60 flex items-center justify-between touch-none"
          >
            <CountryLabel code={code} size="md" variant="overlay" />
            <div className="flex items-center gap-1 text-white/60">
              <span className="text-[10px] uppercase tracking-wide">Drag</span>
              <GripVertical className="w-4 h-4" />
            </div>
          </div>
        </div>
      </div>

      {/* Country Info Modal - using shared component */}
      <CountryInfoModal
        code={code}
        open={showInfo}
        onOpenChange={setShowInfo}
        showMap={true}
      />
    </>
  );
}
